import { Button, View } from "react-native-ui-lib";
import {
  CheckoutRootParamList,
  PaymentRootScreenParamList,
  RegisterRootParamList,
} from "../../../../types";
import { useCallback } from "react";
import PaymentMethod from "../../../../utilities/data/model/PaymentMethod";
import { MakeCashPayment } from "./components/MakeCashPayment";
import Sale from "../../../../utilities/data/model/Sale";
import { useCheckoutContextProvider } from "../../utils/checkout";
import { usePaymentControllerProvider } from "../../utils/payment-controller";
import { MakeTyroPayment } from "./components/MakeTyroPayment";
import { StackScreenProps } from "@react-navigation/stack";
import { DefaultPayment } from "./components/DefaultPayment/DefaultPayment";
import { MakeLinklyPayment } from "./components/MakeLinklyPayment";
import React from "react";

import {
  cashRoundingPaymentMethod,
  roundCashNearest05,
} from "../../../../utilities/round-cash";
import { isValidMoneyValue } from "../../../../utilities/is-valid-money";
import {
  compareMoreThanWithTolerance,
  compareWithTolerance,
} from "../../../../utilities/compare-with-tolerance";
import { omit } from "lodash";
import {
  usePaymentActionsContextProvider,
  usePaymentRootScreenContextProvider,
} from "../PaymentRootScreen/hooks";
import {
  OnAddPaymentFn,
  OnAfterPaymentFn,
  PaymentActionsContext,
} from "../PaymentRootScreen/context";
import { MakeSmartPayPayment } from "./components/MakeSmartPayPayment";
import { useGlobalSettingsProvider } from "../../../../utilities/global-settings/hooks";
import { MakeTowerPayment } from "./components/MakeTowerPayment";
export type PaymentScreenProps = StackScreenProps<
  PaymentRootScreenParamList & RegisterRootParamList & CheckoutRootParamList,
  "PaymentScreen"
>;

export const PaymentScreen = ({ route, navigation }: PaymentScreenProps) => {
  const { sale, cashManagedPaymentMethod, roundingPaymentMethod, terminal } =
    useCheckoutContextProvider();

  const globalSettings = useGlobalSettingsProvider();

  const paymentRootContext = usePaymentRootScreenContextProvider();
  const maybeParentScreenContext = usePaymentActionsContextProvider();

  const { addPayment } = usePaymentControllerProvider();

  const paymentMethod = paymentRootContext.paymentMethod;
  const minTenderAmount = paymentRootContext.minTenderAmount;

  const handleOnAfterPayment: OnAfterPaymentFn = React.useCallback(
    async (canClose) => {
      /**
       * One last chance to modify the sale (or to highjack the navigation), for example,
       * converting the sale to a LayBy, Invoice or Quote
       *
       * returning false here will prevent the screen from closing,
       * and it is up to the implementer to close the screen
       */
      if (maybeParentScreenContext?.onAfterPayment) {
        const result = await maybeParentScreenContext?.onAfterPayment(canClose);
        if (result === false) {
          return;
        }
      }
      if (false === canClose) {
        /**
         * If the sale can't be closed, we should navigate back to the payment method selection screen
         */

        navigation.pop();
      } else {
        navigation.reset({
          index: 0,
          routes: [{ name: "CheckoutSummaryRootScreen" }],
        });
      }
    },
    [maybeParentScreenContext]
  );

  /*
  This is an internal function that is called by child components
  there is no need to implement this in parent screens
  */
  const handleOnAddPayment: OnAddPaymentFn = React.useCallback(
    async (options) => {
      const isCashPayment = options.paymentMethod.payType.id === "1";

      const paymentMethod = options.paymentMethod;

      const balance = await sale.balance_ASYNC();

      const roundedBalance = isCashPayment
        ? cashRoundingPaymentMethod(balance, paymentMethod)
        : balance;

      const paymentAmount = options.paymentAmount;
      const tenderedAmount = options.tenderedAmount;

      if (
        !isValidMoneyValue(paymentAmount) ||
        !isValidMoneyValue(tenderedAmount)
      ) {
        throw new Error("Invalid payment amount");
      }

      const normalizedTenderedAmount = parseFloat(tenderedAmount);
      const normalizedPaymentAmount = parseFloat(paymentAmount);

      const willCompleteSale = compareWithTolerance(
        parseFloat(paymentAmount),
        roundedBalance
      );

      const overTenderPayment = compareMoreThanWithTolerance(
        normalizedTenderedAmount,
        normalizedPaymentAmount
      );

      /**
       * As of writing this, there is no way to determine if a payment method
       * is allowed to provide cash back. This is a temporary solution where if the payment method
       * is cash, and the tendered amount is greater than the payment amount, we will allow cash back
       */
      if (!willCompleteSale && paymentMethod.payType.id === "1") {
        if (overTenderPayment) {
          const change = normalizedTenderedAmount - normalizedPaymentAmount;
          return navigation.navigate("GiveChangeBackScreen", {
            changeAmount: change,
            paymentAmount: parseFloat(paymentAmount),
          });
        }
      }

      await addPayment(paymentMethod, {
        ...omit(options, ["tenderedAmount", "paymentAmount"]),
        tenderedAmount: normalizedTenderedAmount,
        paymentAmount: normalizedPaymentAmount,
      });

      const canClose = await sale.canTransitionToClosed_ASYNC();
      await handleOnAfterPayment(canClose);
    },
    []
  );

  const renderPaymentComponent = useCallback(
    (payTypeId: string, sale: Sale, paymentMethod: PaymentMethod) => {
      switch (payTypeId) {
        case "10":
          return (
            <MakeTyroPayment
              mode="PROD"
              navigation={navigation}
              sale={sale}
              paymentMethod={paymentMethod}
            />
          );
        case "15":
          return (
            <MakeTyroPayment
              mode="DEV"
              navigation={navigation}
              sale={sale}
              paymentMethod={paymentMethod}
            />
          );

        case "1":
          return (
            <MakeCashPayment
              minTenderAmount={minTenderAmount}
              sale={sale}
              paymentMethod={paymentMethod}
            />
          );
        case "30":
          return (
            <MakeLinklyPayment
              navigation={navigation}
              sale={sale}
              paymentMethod={paymentMethod}
            />
          );
        case "40":
          return (
            <MakeSmartPayPayment
              navigation={navigation}
              sale={sale}
              paymentMethod={paymentMethod}
              currentTerminal={terminal}
              globalSettings={globalSettings}
            />
          );
        case "45":
          return (
            <MakeTowerPayment
              navigation={navigation}
              sale={sale}
              paymentMethod={paymentMethod}
              currentTerminal={terminal}
              globalSettings={globalSettings}
            />
          );
        default:
          return (
            <DefaultPayment
              navigation={navigation}
              sale={sale}
              paymentMethod={paymentMethod}
              minTenderAmount={minTenderAmount}
            />
          );
      }
    },
    [minTenderAmount, paymentMethod.id]
  );

  if (!paymentMethod) {
    return <View />;
  }

  return (
    <PaymentActionsContext.Provider
      value={{
        onAfterPayment: handleOnAfterPayment,
        onAddPayment: handleOnAddPayment,
      }}
    >
      {sale && cashManagedPaymentMethod && roundingPaymentMethod
        ? renderPaymentComponent(paymentMethod.payType.id, sale, paymentMethod)
        : null}
    </PaymentActionsContext.Provider>
  );
};
